import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { logWithState } from '@gimlite/common';
import { useStoreUIV1 } from '../hooks/useStore.hook';
import { RootStoreMobX } from './root.store';

type WizardType = {
  id: string;
  component: React.ComponentType<any>;
};

type WizardStoreType = any;

export type WizardStoreContextType = {
  selected: { wizard: WizardType; store?: WizardStoreType };
  registry: Array<WizardType>;
};

export class WizardStoreMobX {
  rootStore: RootStoreMobX;
  @observable public load: boolean = false;
  @observable public selected: WizardStoreContextType['selected'] | null = null;
  @observable public nextSelected: WizardStoreContextType['selected'] | null =
    null;
  @observable public registry: WizardStoreContextType['registry'] = [];

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @computed get hasSelected(): boolean {
    return this.selected !== null;
  }

  @computed get hasNextSelected(): boolean {
    return this.nextSelected !== null;
  }

  @computed get registryCount(): number {
    return this.registry.length;
  }

  @action public init({
    registry,
  }: {
    registry: WizardStoreContextType['registry'];
  }) {
    runInAction(() => {
      this.load = true;
      this.registry = [...this.registry, ...registry];
    });
  }

  @action public select(
    item: {
      id: WizardType['id'];
      store?: WizardStoreType['store'];
    } | null,
  ) {
    if (!item) {
      runInAction(() => {
        this.selected = null;
      });
      setTimeout(() => {
        runInAction(() => {
          this.selected = this.nextSelected ?? null;
        });
      }, 100);
      return;
    }

    const { id, store } = item;
    if (this.load) {
      const selectedWizard = this.registry.find((item) => item.id === id);

      if (!!selectedWizard) {
        runInAction(() => {
          this.selected = { wizard: selectedWizard, store };
        });
      }
    }
  }

  @action public nextSelect(
    item: {
      id: WizardType['id'];
      store?: WizardStoreType['store'];
    } | null,
  ) {
    if (!item) {
      runInAction(() => {
        this.nextSelected = null;
      });
      return;
    }

    const { id, store } = item;

    if (this.load) {
      const selectedWizard = this.registry.find((item) => item.id === id);

      if (!!selectedWizard) {
        runInAction(() => {
          this.nextSelected = { wizard: selectedWizard, store };
        });
      }
    }
  }

  @action public updateStore(record: Record<string, any>) {
    if (this.selected?.store) {
      runInAction(() => {
        this.selected!.store = { ...this.selected!.store, ...record };
      });
    }
  }

  @action public clear() {
    try {
      runInAction(() => {
        this.selected = null;
      });
    } catch (e) {}
  }
}

export const ConfigWizard = observer(
  ({
    children,
    registry,
  }: {
    children: React.ReactNode;
    registry: WizardStoreContextType['registry'];
  }) => {
    const { WizardStore } = useStoreUIV1();

    useEffect(() => {
      if (!WizardStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigWizard init' });
        WizardStore.init({ registry });
      }

      if (WizardStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigWizard load' });
      }
    }, [WizardStore.load]);

    return WizardStore.load ? children : null;
  },
);
